/**
 * BPTargetsView — the « Contenu » and « Produits » sub-tabs of a Brand Perception axis
 * dashboard (docs/plans/bp-dashboard.md §5.2, lot 7).
 *
 * Presentational: the ONE debounced fetch stays in `BrandAxisDashboard`, which owns the
 * timeframe, the filter bar and the `…/dashboard/targets` request. This file turns one
 * `BPTargetsResponse` into tiles + three cards, and nothing else.
 *
 * The four rules the response's shape imposes here, each of which is a way of NOT
 * reporting an absence as a measurement:
 *
 *  1. **An absent `BPTOP` row is NOT MEASURED, never zero.** A day with no Cibles
 *     measurement writes no row at all, so `responseTotal === 0` over the whole window
 *     means « jamais/pas mesuré » and gets a panel, not a leaderboard of zeros.
 *     `unmeasuredBuckets` names the individual holes inside an otherwise measured window.
 *  2. **There is a data floor.** `BPTOP` / `BPTGTAGG` are not backfillable, so a window
 *     reaching before `availableFrom` states « Données disponibles à partir du ⟨date⟩ »
 *     instead of drawing an empty left-hand side. No `availableFrom` at all = never
 *     measured, and then no date is named either.
 *  3. **`partial`, `topTruncated`, `by_domain_truncated` and `by_tag_stale` all make the
 *     figures LOWER BOUNDS.** A truncated channel list silently reads as "we only sell on
 *     200 domains", so every one of them is surfaced.
 *  4. **Target LABELS may be absent and must not be faked.** `BPTopEntry.l` is optional
 *     because the synthesizer cannot reach it (`bp_facts._fold_targets`: the `.idx` the
 *     matcher loads carries no label); a read-time display-metadata join is planned
 *     but NOT BUILT for this view (docs/features/targets.md §19-§20), and `u` (the
 *     cible's own page) will come from the same join and is what makes a row a link. A row the join misses — a cible imported before the
 *     projection existed, or one genuinely gone — renders a muted, explicitly-provisional
 *     short code derived from the id, and is NOT a link: never a bare 36-char uuid, never
 *     a name reconstructed from another field (`TargetMatch.value` is a FOLDED probe,
 *     i.e. a highlight term), never a guessed URL.
 *
 * No content-type dimension is offered: `BPTopRow.byContentType` is never written and
 * `ty` is absent from `top[]` for the same reason as `l`, so the dimension cannot be
 * populated at all. A permanently-empty chart is worse than no chart.
 *
 * No time series either: the response is a single window-wide fold with no per-bucket
 * points, and `BPTGTAGG` (month-grained, first-writer-wins per day) deliberately does
 * not reconcile with the daily `BPTOP` leaderboard — so nothing here is derived from
 * anything else here.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Typography } from '@mui/material';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import {
  BarRows,
  CARD,
  ChartCard,
  EmptyNote,
  EmptyPanel,
  NoticePanel,
  StatTile,
  VizTable,
  VIZ,
  fmtInt,
  fmtPct,
  type BarRow,
} from '../viz';
import { BP_MAX_SPAN_DAYS } from './bpRange';
import type { TargetLabel } from './targetLabels';
import { CAT_BY_ID, tagRegistryOf } from '../../pages/targets/CATEGORIES';
// The real mark, from the app's own favicon service (S3-cached, cross-tenant, with a
// deterministic letter avatar while it loads or when the domain genuinely has none).
// The mock draws a letter badge here only because a static mock cannot fetch one.
import DomainFavicon from '../competitors/DomainFavicon';
import { sourceHref } from '../../utils/sourceUrl';
import type { BPTargetsResponse, BPTopEntry, TargetsManifest } from '@traaker/shared';

/** Plotted rows per chart. Beyond this the SVG grows taller than the viewport; the
 *  `VizTable` twin keeps the full list, and the chart says how many it left out. */
const CHART_ROWS = 15;
/** Rows kept in a table twin. `top[]` can carry 500 entries × 6 cells. */
const TABLE_ROWS = 100;
/** Dates named inline before the list becomes noise. */
const DATES_INLINE = 6;

export interface BPTargetsViewProps {
  /** Which sub-tab this is. Only changes copy — the category scope is applied by the
   *  caller, as the `category` query param. */
  kind: 'contenu' | 'produit' | 'personne';
  data: BPTargetsResponse;
  /** `/targets/summary` — the Cibles tag registry lives in it (id → name). */
  manifest: TargetsManifest | null;
  /**
   * `targetId → {label, url}` from the by-id join the caller owns. Empty while it is
   * in flight or when it failed — the label chain degrades to url → domain → id code,
   * which is exactly the no-join behaviour, so nothing here waits on it.
   */
  labels: Map<string, TargetLabel>;
  /** The client-side 90-day clamp, which the response cannot report on itself. */
  spanClamped: boolean;
}

/** Flat panel for a state that is an absence of measurement rather than a result. */
function NotePanel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: `${CARD.padY * 8}px ${CARD.padX * 8}px`,
        borderRadius: `${CARD.radius}px`,
        border: `1px solid ${VIZ.border}`,
        boxShadow: 'none',
      }}
    >
      <Typography sx={{ fontSize: 14, fontWeight: 600, color: VIZ.ink, mb: 0.75 }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: 13.5, lineHeight: 1.45, color: VIZ.muted }}>
        {children}
      </Typography>
    </Paper>
  );
}

/** Category chip (mock `.pill`). */
function Pill({ children }: { children: React.ReactNode }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 22,
        px: 1.125,
        borderRadius: '99px',
        bgcolor: VIZ.lavender,
        color: '#5A2FC4',
        fontSize: 12,
        fontWeight: 500,
      }}
    >
      {children}
    </Box>
  );
}

export default function BPTargetsView({
  kind,
  data,
  manifest,
  labels,
  spanClamped,
}: BPTargetsViewProps) {
  const { t, i18n } = useTranslation();
  /** Suffix of the kind-specific copy keys (`bpTargets.topTitleProduit`, …). */
  const kindKey = kind === 'produit' ? 'Produit' : kind === 'personne' ? 'Personne' : 'Contenu';

  const fmtDay = (key: string) => {
    if (!key) return '';
    if (key.length === 7) {
      return new Date(`${key}-01T00:00:00Z`).toLocaleDateString(i18n.language, {
        month: 'short',
        year: '2-digit',
        timeZone: 'UTC',
      });
    }
    return new Date(`${key}T00:00:00Z`).toLocaleDateString(i18n.language, {
      day: 'numeric',
      month: 'short',
      timeZone: 'UTC',
    });
  };
  const dateList = (days: string[]) =>
    days.length <= DATES_INLINE
      ? days.map(fmtDay).join(', ')
      : `${days.slice(0, DATES_INLINE).map(fmtDay).join(', ')} …`;

  // Tag NAMES come from the manifest registry, never from the id: `byTag` and
  // `BPTopEntry.tg` carry uint16 ids, and a bare id on screen is not a label. An id
  // with no entry is a RETIRED tag (ids are never reused) — it is labelled as deleted
  // rather than dropped, because dropping a bucket would silently lower the chart.
  const registry = useMemo(() => tagRegistryOf(manifest), [manifest]);
  const tagLabel = (id: number | string) =>
    registry.get(Number(id)) ?? t('bpTargets.tagRetired', { id: String(id) });

  const emptyNote = t('bpDash.noData');
  const viewLabels = { chartLabel: t('bpDash.viewChart'), tableLabel: t('bpDash.viewTable') };

  // ---- labels: present, or explicitly provisional ---------------------------

  /**
   * The row's name and page. The JOIN wins over the stored fields: `l` / `u` are
   * never written (the fold cannot reach a catalogue field), so they are only ever a
   * fallback for a response that somehow carries them.
   */
  const nameOf = (e: BPTopEntry) => labels.get(e.id)?.label || e.l;
  const urlOf = (e: BPTopEntry) => labels.get(e.id)?.url || e.u;

  const labelsPending = data.top.some((e) => !nameOf(e));
  /**
   * The cible's URL, shown AS a URL — the identifier of last resort before the id code.
   *
   * This is not a reconstructed name and must never become one: no slug prettifying, no
   * title-casing, no stripping of the path down to a word. A great many `produit` /
   * `collection` cibles are imported with `id` + `url` and NO `name` (15 314 of 96 092
   * rows in dev, 2026-10-06), so their label is genuinely absent in the catalogue and no
   * join can resolve it. Printing the url the user themselves declared identifies the row
   * and is visibly a url; inventing « Shampoing X » out of `/p/shampoing-x` would be a
   * product name we made up. Only the scheme and a trailing slash are dropped.
   */
  const urlLabel = (u?: string) => {
    if (!u) return '';
    const compact = u.replace(/^https?:\/\//i, '').replace(/\/+$/, '');
    return compact.length > 64 ? `${compact.slice(0, 63)}…` : compact;
  };
  /**
   * The row's DOMAIN — the host of its own url when the join resolved one, else the
   * busiest of the apex domains the matcher recorded for it (`d`, ≤10 entries, the
   * same map the channels card is folded from).
   *
   * This is the identity an unnamed row actually has: a cible with no `name` in the
   * catalogue is still a page on a site, and « wikipedia.org » + its favicon says far
   * more than a 6-char id prefix. `d` saturates at 10 keys, so the pick is "busiest
   * of the ones kept", never "the only one" — fine for a mark, which is why it is not
   * used for any count.
   */
  const domainOf = (e: BPTopEntry): string | undefined => {
    const u = urlOf(e);
    if (u) {
      try {
        // Catalogue urls are stored scheme-less (`site.com/p/x`).
        return new URL(sourceHref(u) ?? u).hostname.replace(/^www\./i, '');
      } catch {
        // A stored value that is not a parseable URL — fall through to `d`.
      }
    }
    const entries = Object.entries(e.d ?? {});
    if (!entries.length) return undefined;
    return entries.sort((a, b) => b[1] - a[1])[0][0];
  };

  /** Unique, so two same-named products cannot collapse onto one chart row. */
  const labelsOf = useMemo(() => {
    const out = new Map<string, string>();
    const used = new Set<string>();
    for (const e of data.top) {
      const base =
        nameOf(e) ||
        urlLabel(urlOf(e)) ||
        domainOf(e) ||
        t('bpTargets.unnamedTarget', { code: e.id.slice(0, 6) });
      out.set(e.id, used.has(base) ? `${base} · ${e.id.slice(0, 6)}` : base);
      used.add(base);
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.top, labels, i18n.language]);
  const labelOf = (e: BPTopEntry) => labelsOf.get(e.id) ?? e.id.slice(0, 6);
  /**
   * The cible's own page, when the (planned) display-metadata join resolved one. A row with no `u`
   * is simply not a link — never a link to a guessed URL, and never to the Cibles list
   * (that would navigate away from the dashboard to a page that cannot name the row).
   */
  const hrefOf = (e: BPTopEntry) => sourceHref(urlOf(e)) ?? undefined;

  /**
   * Which of the three identifiers a row ended up with. The id-code case gets the
   * mock's `.unresolved` + `.code` treatment — italic prose plus the code in a mono
   * chip — because an id is not prose, and setting it in the same type as a product
   * name is what made « Cible non résolue · f4df0e » read as a product called that.
   */
  const labelNodeOf = (e: BPTopEntry) => {
    // A name, a url or a domain: plain text is correct, and the favicon is the mark.
    if (nameOf(e) || urlOf(e) || domainOf(e)) return undefined;
    return (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, color: VIZ.muted, fontStyle: 'italic' }}>
        {t('bpTargets.unnamedTargetBare')}
        <Box
          component="span"
          sx={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontStyle: 'normal',
            fontSize: 12,
            color: VIZ.ink2,
            bgcolor: '#F3F3F5',
            border: '1px solid #E8E8EC',
            borderRadius: '6px',
            px: 0.875,
            py: '2px',
          }}
        >
          {e.id.slice(0, 6)}
        </Box>
      </Box>
    );
  };

  /** The row's favicon, when a domain is known. Absent rather than a letter box. */
  const targetMark = (e: BPTopEntry) => {
    const d = domainOf(e);
    return d ? <DomainFavicon domain={d} name={d} size={20} /> : undefined;
  };

  const labelCell = (e: BPTopEntry) => {
    const node = labelNodeOf(e);
    const text =
      node ??
      (nameOf(e) ? (
        labelOf(e)
      ) : (
        <Box component="span" sx={{ color: VIZ.muted, fontStyle: 'italic' }}>
          {labelOf(e)}
        </Box>
      ));
    const href = hrefOf(e);
    if (!href) return text;
    return (
      <Box
        component="a"
        href={href}
        target="_blank"
        rel="noreferrer"
        title={href}
        sx={{ color: 'inherit', textDecoration: 'underline', textDecorationColor: VIZ.axis }}
      >
        {text}
      </Box>
    );
  };

  // ---- caveats --------------------------------------------------------------

  const caveatLines = useMemo(() => {
    const out: string[] = [];
    if (spanClamped) out.push(t('bpDash.caveat.spanClamped', { days: fmtInt(BP_MAX_SPAN_DAYS) }));
    if (data.clamped) out.push(t('bpDash.caveat.clamped'));
    // The floor, stated WITH its date — the bare `window_precedes_target_data` code
    // cannot name one, so it is replaced here rather than printed twice.
    if (data.availableFrom && data.from < data.availableFrom) {
      out.push(t('bpTargets.availableFrom', { date: fmtDay(data.availableFrom) }));
    }
    for (const c of data.caveats) {
      if (c === 'window_precedes_target_data') continue; // stated above, with the date
      if (c === 'target_days_unmeasured') continue; // stated below, with the days
      out.push(t(`bpDash.caveat.${c}`));
    }
    if (data.unmeasuredBuckets?.length) {
      out.push(
        t('bpTargets.unmeasuredDays', {
          n: fmtInt(data.unmeasuredBuckets.length),
          days: dateList(data.unmeasuredBuckets),
        }),
      );
    }
    if (data.partialBuckets?.length) {
      out.push(t('bpTargets.partialDays', { days: dateList(data.partialBuckets) }));
    }
    if (data.topTruncated) out.push(t('bpTargets.topTruncated'));
    if (labelsPending) out.push(t('bpTargets.labelsPending'));
    out.push(t('bpTargets.monthVsDay'));
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, spanClamped, labelsPending, i18n.language]);

  // ---- the two absence states -----------------------------------------------
  //
  // They are DIFFERENT statements and must never borrow each other's copy:
  //   1. target data was NEVER written for this axis;
  //   2. it was written, but not inside this window.
  // There is no « détail expiré » state any more: past 90 days the read goes to the
  // written monthly tier (`BPTOPM`, Lot 9, 2026-10-14), so an empty month window is (2).

  // No floor row at all ⇒ no audit of this axis has EVER written target data. There is
  // no date to name, so the panel says « jamais mesuré » instead (§2 of the header).
  if (!data.availableFrom) {
    return <NotePanel title={t('bpTargets.neverMeasuredTitle')}>{t('bpTargets.neverMeasured')}</NotePanel>;
  }

  // `responseTotal` is `len(rows)` on every row the writer emits, so 0 over the window
  // means NO `BPTOP` row was read — « pas mesuré », not « 0 citation ».
  if (data.responseTotal === 0) {
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <NotePanel title={t('bpTargets.notMeasuredInWindowTitle')}>
          {t('bpTargets.notMeasuredInWindow')}
        </NotePanel>
        <NoticePanel title={t('bpDash.caveatsTitle')} lines={caveatLines} />
      </Box>
    );
  }

  // ---- derived view models --------------------------------------------------

  // Folded from `top[]` rather than from `byCategory`: the request is always scoped to
  // ONE category, so the service re-folds `byCategory` from this very array — reading
  // it back would be the same number with one more hop.
  const citations = data.top.reduce((s, e) => s + (e.c ?? 0), 0);

  const domainRows = useMemo(
    () =>
      Object.entries(data.byDomain)
        .map(([domain, b]) => ({ domain, ...b, topTag: data.topTagByDomain[domain] }))
        .sort((a, b) => b.c - a.c),
    [data.byDomain, data.topTagByDomain],
  );

  /** « 2 citations » — the right column of every bar row on these two tabs. */
  const nCitations = (n: number) => (
    <>
      <b>{fmtInt(n)}</b> {t('bpTargets.nCitations', { count: n })}
    </>
  );

  const domainChart: BarRow[] = domainRows.slice(0, CHART_ROWS).map((r) => ({
    key: r.domain,
    label: r.domain,
    value: r.c,
    color: VIZ.good,
    mark: <DomainFavicon domain={r.domain} name={r.domain} size={22} />,
    valueLabel: nCitations(r.c),
  }));

  const tagRows = useMemo(
    () =>
      Object.entries(data.byTag)
        .map(([id, b]) => ({ id, ...b }))
        .sort((a, b) => b.c - a.c),
    [data.byTag],
  );

  const tagChart: BarRow[] = tagRows.slice(0, CHART_ROWS).map((r) => ({
    key: r.id,
    label: tagLabel(r.id),
    value: r.c,
    color: VIZ.serious,
    valueLabel: nCitations(r.c),
  }));

  const topChart: BarRow[] = data.top.slice(0, CHART_ROWS).map((e) => ({
    key: e.id,
    label: labelOf(e),
    labelNode: labelNodeOf(e),
    mark: targetMark(e),
    value: e.c,
    href: hrefOf(e),
    // Italic + muted when the name is NOT the thing's name but a url or an id code.
    provisional: !nameOf(e),
    valueLabel: nCitations(e.c),
  }));

  const capNote = (shown: number, total: number) =>
    total > shown ? (
      <Typography sx={{ fontSize: 11.5, color: VIZ.muted, mt: 1 }}>
        {t('bpTargets.chartCap', { n: fmtInt(shown) })}
      </Typography>
    ) : null;

  const tableCapNote = (total: number) =>
    total > TABLE_ROWS ? (
      <Typography sx={{ fontSize: 11.5, color: VIZ.muted, mt: 1 }}>
        {t('bpTargets.tableCap', { n: fmtInt(TABLE_ROWS), total: fmtInt(total) })}
      </Typography>
    ) : null;

  // ---- render ---------------------------------------------------------------

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${CARD.gap * 8}px` }}>
      <NoticePanel title={t('bpDash.caveatsTitle')} lines={caveatLines} />

      {/* ---- KPI row ------------------------------------------------------- */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
        <StatTile
          sx={{ flex: '1 1 220px' }}
          label={t(`bpTargets.kpiCitations${kindKey}`)}
          dot={VIZ.violet}
          value={fmtInt(citations)}
          foot={t('bpTargets.kpiCitationsFoot', { n: fmtInt(data.responseTotal) })}
        />
        <StatTile
          sx={{ flex: '1 1 220px' }}
          label={t('bpTargets.kpiCited')}
          dot={VIZ.serious}
          value={fmtInt(data.top.length)}
          foot={data.topTruncated ? t('bpTargets.kpiCitedFootTruncated') : t('bpTargets.kpiCitedFoot')}
        />
        <StatTile
          sx={{ flex: '1 1 220px' }}
          label={t(`bpTargets.kpiChannels${kindKey}`)}
          dot={VIZ.good}
          value={fmtInt(domainRows.length)}
          foot={
            data.caveats.includes('by_domain_truncated')
              ? t('bpTargets.kpiChannelsFootCapped')
              : t('bpTargets.kpiChannelsFoot')
          }
        />
        <StatTile
          sx={{ flex: '1 1 220px' }}
          label={t('bpTargets.kpiPrompts')}
          dot={VIZ.ink}
          value={fmtInt(data.promptTotal)}
          foot={t('bpTargets.kpiPromptsFoot')}
        />
      </Box>

      {/* ---- canaux de vente / domaines citants ---------------------------- */}
      <ChartCard
        title={t(`bpTargets.channelsTitle${kindKey}`)}
        dot={VIZ.good}
        hint={t(`bpTargets.channelsHint${kindKey}`)}
        {...viewLabels}
        table={
          <VizTable
            columns={[
              t('bpTargets.colDomain'),
              t('bpTargets.colCitations'),
              t('bpTargets.colShare'),
              t('bpTargets.colTargets'),
              t('bpTargets.colTopTag'),
            ]}
            align={['left', 'right', 'right', 'right', 'left']}
            rows={domainRows.map((r) => [
              <Box key="d" component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.25, fontWeight: 500 }}>
                <DomainFavicon domain={r.domain} name={r.domain} size={22} />
                {r.domain}
              </Box>,
              fmtInt(r.c),
              // The share gets its own 100px bar in the table too (mock `.share`):
              // « 100,0 % » on one row means something different from on ten.
              r.share != null ? (
                <Box key="s" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: 'flex-end' }}>
                  <Box sx={{ width: 100, height: 6, bgcolor: VIZ.track, borderRadius: '99px', overflow: 'hidden' }}>
                    <Box sx={{ height: '100%', width: `${r.share * 100}%`, bgcolor: VIZ.good, borderRadius: '99px' }} />
                  </Box>
                  <span>{fmtPct(r.share)}</span>
                </Box>
              ) : (
                <Box key="s" component="span" sx={{ color: VIZ.nodata }}>{t('bpDash.notMeasuredShort')}</Box>
              ),
              fmtInt(r.targets),
              r.topTag ? tagLabel(r.topTag.tag) : <Box key="tt" component="span" sx={{ color: VIZ.nodata }}>—</Box>,
            ])}
          />
        }
      >
        {domainChart.length === 0 ? (
          <EmptyNote>{t('bpTargets.channelsEmpty')}</EmptyNote>
        ) : (
          <>
            <BarRows rows={domainChart} emptyNote={emptyNote} />
            {capNote(domainChart.length, domainRows.length)}
          </>
        )}
      </ChartCard>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: `${CARD.gap * 8}px` }}>
        {/* ---- tag segmentation -------------------------------------------- */}
        <Box sx={{ flex: '1 1 520px', minWidth: 0 }}>
          <ChartCard
            title={t('bpTargets.tagsTitle')}
            dot={VIZ.serious}
            hint={t('bpTargets.tagsHint')}
            {...viewLabels}
            table={
              <VizTable
                columns={[t('bpTargets.colTag'), t('bpTargets.colCitations'), t('bpTargets.colTargets')]}
                align={['left', 'right', 'right']}
                rows={tagRows.map((r) => [tagLabel(r.id), fmtInt(r.c), fmtInt(r.targets)])}
              />
            }
          >
            {tagChart.length === 0 ? (
              // An untagged referential is an ACTION, not an absence of data — so it
              // gets the panel with the next step in it, not a bare "no data" line.
              <EmptyPanel icon={<SellOutlinedIcon sx={{ fontSize: 18 }} />}>
                {t('bpTargets.tagsEmpty')}
              </EmptyPanel>
            ) : (
              <>
                <BarRows rows={tagChart} emptyNote={emptyNote} />
                {capNote(tagChart.length, tagRows.length)}
              </>
            )}
          </ChartCard>
        </Box>

        {/* ---- the per-target leaderboard ---------------------------------- */}
        <Box sx={{ flex: '1 1 520px', minWidth: 0 }}>
          <ChartCard
            title={t(`bpTargets.topTitle${kindKey}`)}
            dot={VIZ.violet}
            hint={t('bpTargets.topHint')}
            {...viewLabels}
            table={
              <VizTable
                columns={[
                  t(`bpTargets.colTarget${kindKey}`),
                  t('bpTargets.colCategory'),
                  t('bpTargets.colCitations'),
                  t('bpTargets.colPrompts'),
                  t('bpTargets.colChannels'),
                  t('bpTargets.colTags'),
                ]}
                align={['left', 'left', 'right', 'right', 'right', 'left']}
                rows={data.top.slice(0, TABLE_ROWS).map((e) => [
                  <Box key="t" component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.25 }}>
                    {targetMark(e)}
                    {labelCell(e)}
                  </Box>,
                  e.ct ? <Pill key="c">{t(CAT_BY_ID[e.ct].labelKey)}</Pill> : <Box key="c" component="span" sx={{ color: VIZ.nodata }}>—</Box>,
                  fmtInt(e.c),
                  e.p != null ? fmtInt(e.p) : <Box key="p" component="span" sx={{ color: VIZ.nodata }}>{t('bpDash.notMeasuredShort')}</Box>,
                  fmtInt(Object.keys(e.d ?? {}).length),
                  (e.tg ?? []).map(tagLabel).join(', ') || <Box key="tg" component="span" sx={{ color: VIZ.nodata }}>—</Box>,
                ])}
              />
            }
          >
            {topChart.length === 0 ? (
              <EmptyNote>{t(`bpTargets.topEmpty${kindKey}`)}</EmptyNote>
            ) : (
              <>
                {/* Wider gutter than the mock's 200px: a cible with no `name` is
                    labelled by its URL, which is far longer than a product name. It
                    still ellipsises — the full value is in the row's `title`. */}
                <BarRows rows={topChart} labelWidth={240} emptyNote={emptyNote} />
                {capNote(topChart.length, data.top.length)}
                {tableCapNote(data.top.length)}
              </>
            )}
          </ChartCard>
        </Box>
      </Box>
    </Box>
  );
}
